import { useEffect, useMemo, useState } from "react";
import { BellRing, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/push";
import { Section } from "@/components/common/DetailFields";

const KIND_LABELS = {
  paso_vencido: "Próximo paso vencido", paso_hoy: "Próximo paso hoy", estancado: "Ya pasó tiempo (sin contacto)",
  comision_por_cobrar: "Comisión por cobrar atrasada", acuerdo_verbal: "Acuerdo verbal sin confirmar", vencimiento: "Vencimientos de inversiones/préstamos", revisar: "Capturas por revisar",
};

const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center justify-between gap-3 py-2 text-sm cursor-pointer">
    <span>{label}</span>
    <input data-testid={testid} type="checkbox" className="w-5 h-5 accent-[#bc9538]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
  </label>
);

const PushControls = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setOn(!!s)); }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); toast.success("Avisos desactivados en este dispositivo"); } else { await enablePush(); setOn(true); toast.success("Avisos activados"); }
    } catch (e) { toast.error(e.message || errMsg(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    try { const { data } = await api.post("/push/test"); toast.success(data.sent_to ? "Aviso de prueba enviado" : "No hay dispositivos suscritos"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 pb-3 border-b c-line">
      <button data-testid="push-toggle-btn" disabled={busy || !pushSupported()} onClick={toggle} className={`w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${on ? "c-card" : "c-btn-primary"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} {on ? "Desactivar avisos en este dispositivo" : "Activar avisos"}
      </button>
      {on && <button data-testid="push-test-btn" onClick={test} className="w-full py-2 rounded-xl c-card text-sm font-semibold flex items-center justify-center gap-1.5"><Send className="w-4 h-4" /> Enviar aviso de prueba</button>}
      <p data-testid="push-status" className="text-xs c-muted">{!pushSupported() ? "Este navegador no soporta avisos. En iPhone instala la app (iOS 16.4+)." : on ? "Avisos activos en este dispositivo." : "Los avisos nunca muestran nombres ni montos."}</p>
    </div>
  );
};

export const NotificationsSection = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me").then((r) => setP(r.data.prefs)).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const { data } = await api.patch("/me/preferences", patch); setP(data.prefs); toast.success("Preferencias guardadas"); } catch (e) { toast.error(errMsg(e)); }
  };
  const alertKinds = p?.alert_kinds;
  const kinds = useMemo(() => (alertKinds ? Object.entries(KIND_LABELS).filter(([k]) => k in alertKinds) : []), [alertKinds]);
  if (!p) return null;
  return (
    <Section title="Notificaciones" testid="config-notifications">
      <PushControls />
      <Toggle testid="pref-daily-toggle" label="Resumen diario por push" checked={p.daily_summary_enabled} onChange={(v) => save({ daily_summary_enabled: v })} />
      <label className="flex items-center justify-between gap-3 py-2 text-sm">
        <span>Hora del resumen</span>
        <select data-testid="pref-daily-hour-select" className="c-input !w-28 !py-2" value={p.daily_summary_hour} onChange={(e) => save({ daily_summary_hour: Number(e.target.value) })}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
        </select>
      </label>
      <Toggle testid="pref-push-alerts-toggle" label="Push cuando hay alertas nuevas" checked={p.push_alerts} onChange={(v) => save({ push_alerts: v })} />
      <Toggle testid="pref-read-aloud-toggle" label="Leer en voz alta las respuestas" checked={p.read_aloud} onChange={(v) => save({ read_aloud: v })} />
      <label className="flex items-center justify-between gap-3 py-2 text-sm">
        <span>"Ya pasó tiempo" después de (días)</span>
        <input data-testid="pref-stalled-days-input" type="number" min={1} max={365} className="c-input !w-24 !py-2" defaultValue={p.stalled_days}
          onBlur={(e) => Number(e.target.value) !== p.stalled_days && save({ stalled_days: Number(e.target.value) })} />
      </label>
      <p className="text-[11px] uppercase tracking-wider c-muted pt-3">Qué alertas</p>
      {kinds.map(([k, label]) => (
        <Toggle key={k} testid={`pref-kind-${k}`} label={label} checked={p.alert_kinds[k]} onChange={(v) => save({ alert_kinds: { ...p.alert_kinds, [k]: v } })} />
      ))}
    </Section>
  );
};
